import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import InputBox from "components/comman/InputBox";
import SelectBox from "components/comman/SelectBox";
import "../stylesheets/InlineEditor.scss"

// Validation schema
const newInlineEditorSchema = yup.object({
    fixtureType: yup.string().required("Fixture type is required"),
    fixtureName: yup.string().when("fixtureType", {
        is: (val) => val && val !== "" && val !== "custom_sink",
        then: (schema) => schema.required("Fixture name is required"),
        otherwise: (schema) => schema.optional()
    }),
    trapSize: yup.string().when("fixtureType", {
        is: (val) => val && val !== "" && val !== "custom_sink",
        then: (schema) => schema.required("Trap size is required"),
        otherwise: (schema) => schema.optional()
    }),
    dfuType: yup.string().when("fixtureType", {
        is: (val) => val && val !== "" && val !== "custom_sink",
        then: (schema) => schema.required("DFU type is required"),
        otherwise: (schema) => schema.optional()
    }),
    dfuValue: yup.string().when("fixtureType", {
        is: (val) => val && val !== "" && val !== "custom_sink",
        then: (schema) => schema.required("DFU value is required"),
        otherwise: (schema) => schema.optional()
    }),
    gpm: yup.string().when("fixtureType", {
        is: (val) => val && val !== "" && val !== "custom_sink",
        then: (schema) => schema.required("GPM is required"),
        otherwise: (schema) => schema.optional()
    }),
    quantity: yup.number()
        .transform((curr, orig) => orig === '' ? null : curr)
        .nullable()
        .typeError("Quantity is required")
        .when("fixtureType", {
            is: (val) => val === "custom_sink",
            then: (schema) => schema.optional(),
            otherwise: (schema) => schema.required("Quantity is required").min(1, "Quantity must be at least 1")
        }),
    flowRate: yup.string().when("fixtureType", {
        is: (val) => val && val !== "",
        then: (schema) => schema.required("Flow rate is required"),
        otherwise: (schema) => schema.optional()
    }),
    length: yup.number()
        .nullable()
        .transform((curr, orig) => orig === '' ? null : curr)
        .when("fixtureType", {
            is: (val) => val === "custom_sink",
            then: (schema) => schema.required("Length is required").typeError("Must be a number"),
            otherwise: (schema) => schema.optional()
        }),
    width: yup.number()
        .nullable()
        .transform((curr, orig) => orig === '' ? null : curr)
        .when("fixtureType", {
            is: (val) => val === "custom_sink",
            then: (schema) => schema.required("Width is required").typeError("Must be a number"),
            otherwise: (schema) => schema.optional()
        }),
    depth: yup.number()
        .nullable()
        .transform((curr, orig) => orig === '' ? null : curr)
        .when("fixtureType", {
            is: (val) => val === "custom_sink",
            then: (schema) => schema.required("Depth is required").typeError("Must be a number"),
            otherwise: (schema) => schema.optional()
        }),
    compartments: yup.number()
        .nullable()
        .transform((curr, orig) => orig === '' ? null : curr)
        .when("fixtureType", {
            is: (val) => val === "custom_sink",
            then: (schema) => schema.required("Compartments is required").min(1).typeError("Must be a number"),
            otherwise: (schema) => schema.optional()
        }),
});

export const InlineEditor = React.memo(function NewInlineEditor(props) {
    const {
        editForm = {
            included: true,
            fixtureType: "",
            fixtureName: "",
            trapSize: "",
            dfuType: "",
            dfuValue: "",
            gpm: "",
            quantity: 1,
            flowRate: "",
            length: "",
            width: "",
            depth: "",
            compartments: ""
        },
        fixtureTypesList = [],
        onChangeEditForm = () => { },
        onChangeFixtureType = () => { },
        onChangeDfuType = () => { },
        onQuantityChange = () => { },
        onCancel = () => { },
        onSave = () => { },
        getDfuOptions = () => []
    } = props;

    const { handleSubmit, control, setValue, watch, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(newInlineEditorSchema),
        defaultValues: editForm,
        values: editForm,
        shouldUnregister: false,
        mode: "onChange",
    });

    const selectedFixtureType = watch("fixtureType");
    const selectedDfuType = watch("dfuType");

    // ✅ NEW: Check if "Other" option is selected
    const isManualEntry = selectedFixtureType === "other";
    const isCustomSink = selectedFixtureType === "custom_sink";

    const [showCalculationInfo, setShowCalculationInfo] = useState(false);

    // Get DFU options for the selected fixture
    const dfuOptions = getDfuOptions(selectedFixtureType);

    // Show all fields when fixture type is selected
    const showAllFields = selectedFixtureType && selectedFixtureType !== "";



    // Handle form submission
    const handleFormSubmit = (data) => {
        onSave(data);
    };

    // ✅ UPDATED: Handle fixture type change with "Other" support
    const handleFixtureTypeChange = (value) => {
        clearErrors();
        if (value === "other" || value === "custom_sink") {
            // For "Other" or "Custom Sink", clear all fields
            setValue("fixtureType", value);
            setValue("fixtureName", value === "custom_sink" ? "Custom Sink Sizing" : "");
            setValue("trapSize", "");
            setValue("gpm", "");
            setValue("dfuType", "");
            setValue("dfuValue", "");
            setValue("flowRate", "");
            setValue("length", "");
            setValue("width", "");
            setValue("depth", "");
            setValue("compartments", "");
            setValue("image", null);

            onChangeFixtureType(value);
            onChangeEditForm({
                fixtureType: value,
                fixtureName: value === "custom_sink" ? "Custom Sink Sizing" : "",
                trapSize: "",
                gpm: "",
                dfuType: "",
                dfuValue: "",
                flowRate: "",
                length: "",
                width: "",
                depth: "",
                compartments: "",
                image: null
            });
        } else {
            // For predefined fixtures, auto-populate
            const selected = fixtureTypesList.find(f => f.value === value);

            let defaultDfuType = "";
            let defaultDfuValue = "";
            let defaultGpmValue = "";
            let defaultFlowRateValue = "";

            if (selected) {
                if (selected.privateDfu !== null && selected.privateDfu !== undefined) {
                    defaultDfuType = "private";
                    defaultDfuValue = String(selected.privateDfu);
                    if (!isNaN(selected.privateDfu)) {
                        defaultGpmValue = String(selected.privateDfu / 2);
                        const currentQuantity = watch("quantity") || 1;
                        defaultFlowRateValue = String(parseFloat((selected.privateDfu / 2) * currentQuantity).toFixed(2));
                    }
                } else if (selected.publicDfu !== null && selected.publicDfu !== undefined) {
                    defaultDfuType = "public";
                    defaultDfuValue = String(selected.publicDfu);
                    if (!isNaN(selected.publicDfu)) {
                        defaultGpmValue = String(selected.publicDfu / 2);
                        const currentQuantity = watch("quantity") || 1;
                        defaultFlowRateValue = String(parseFloat((selected.publicDfu / 2) * currentQuantity).toFixed(2));
                    }
                }
            }

            setValue("fixtureType", value);
            setValue("fixtureName", selected?.name || "");
            setValue("trapSize", selected?.trapSize || "");
            setValue("gpm", defaultGpmValue);
            setValue("dfuType", defaultDfuType);
            setValue("dfuValue", defaultDfuValue);
            setValue("flowRate", defaultFlowRateValue);
            setValue("image", selected?.image || null);

            onChangeFixtureType(value);
            onChangeEditForm({
                fixtureType: value,
                fixtureName: selected?.name || "",
                trapSize: selected?.trapSize || "",
                gpm: defaultGpmValue,
                dfuType: defaultDfuType,
                dfuValue: defaultDfuValue,
                flowRate: defaultFlowRateValue,
                image: selected?.image || null
            });
        }
    };




    const handleQuantityInputChange = (value) => {
        if (value === "") {
            setValue("quantity", "");
            const currentGpm = parseFloat(watch("gpm"));
            if (currentGpm && !isNaN(currentGpm)) {
                setValue("flowRate", "");
                onChangeEditForm({
                    quantity: "",
                    flowRate: ""
                });
            } else {
                onChangeEditForm({
                    quantity: ""
                });
            }
            return;
        }

        const numValue = parseInt(value);
        if (isNaN(numValue)) {
            setValue("quantity", "");
            const currentGpm = parseFloat(watch("gpm"));
            if (currentGpm && !isNaN(currentGpm)) {
                setValue("flowRate", "");
                onChangeEditForm({
                    quantity: "",
                    flowRate: ""
                });
            } else {
                onChangeEditForm({
                    quantity: ""
                });
            }
            return;
        }

        const validValue = Math.max(0, numValue);
        setValue("quantity", validValue);

        const currentGpm = parseFloat(watch("gpm"));
        if (currentGpm && !isNaN(currentGpm)) {
            const newFlowRate = String(parseFloat(currentGpm * validValue).toFixed(2));
            setValue("flowRate", newFlowRate);

            onChangeEditForm({
                quantity: validValue,
                flowRate: newFlowRate
            });
        } else {
            onChangeEditForm({
                quantity: validValue
            });
        }
    };


    const handleDfuTypeChange = (value) => {
        // If manual entry, just update the field
        if (isManualEntry) {
            setValue("dfuType", value);
            onChangeDfuType(value);
            onChangeEditForm({
                dfuType: value
            });
            return;
        }

        // For predefined fixtures, auto-calculate
        const fixture = fixtureTypesList.find(f => f.value === selectedFixtureType);
        let dfuValue = "";
        let gpmValue = "";
        let flowRateValue = "";

        if (fixture && value === "private") {
            dfuValue = String(fixture.privateDfu || "");
            if (fixture.privateDfu && !isNaN(fixture.privateDfu)) {
                gpmValue = String(fixture.privateDfu / 2);
                const currentQuantity = watch("quantity") || 1;
                flowRateValue = String(parseFloat((fixture.privateDfu / 2) * currentQuantity).toFixed(2));
            }
        } else if (fixture && value === "public") {
            dfuValue = String(fixture.publicDfu || "");
            if (fixture.publicDfu && !isNaN(fixture.publicDfu)) {
                gpmValue = String(fixture.publicDfu / 2);
                const currentQuantity = watch("quantity") || 1;
                flowRateValue = String(parseFloat((fixture.publicDfu / 2) * currentQuantity).toFixed(2));
            }
        }

        setValue("dfuType", value);
        setValue("dfuValue", dfuValue);
        setValue("gpm", gpmValue);
        setValue("flowRate", flowRateValue);

        onChangeDfuType(value);
        onChangeEditForm({
            dfuType: value,
            dfuValue: dfuValue,
            gpm: gpmValue,
            flowRate: flowRateValue
        });
    };

    const handleQuantityChange = (increment) => {
        const currentQuantity = watch("quantity");
        const newQuantity = Math.max(1, currentQuantity + increment);
        const currentGpm = parseFloat(watch("gpm")) || 0;

        const newFlowRate = parseFloat(currentGpm * newQuantity).toFixed(2);

        setValue("quantity", newQuantity);
        setValue("flowRate", String(newFlowRate));

        onQuantityChange(increment);
        onChangeEditForm({
            quantity: newQuantity,
            flowRate: String(newFlowRate)
        });
    };

    // ✅ UPDATED: Handle manual input changes (for "Other" option)
    const handleManualInputChange = (field, value) => {
        setValue(field, value);
        onChangeEditForm({ [field]: value });

        // If GPM changes and manual entry, recalculate flow rate
        // if (field === "gpm" && isManualEntry) {
        //     const gpmValue = parseFloat(value);
        //     if (gpmValue && !isNaN(gpmValue)) {
        //         const currentQuantity = watch("quantity") || 1;
        //         const newFlowRate = String(parseFloat(gpmValue * currentQuantity).toFixed(2));
        //         setValue("flowRate", newFlowRate);
        //         onChangeEditForm({ flowRate: newFlowRate });
        //     } else {

        //         setValue("flowRate", "");
        //         onChangeEditForm({ flowRate: "" });
        //     }
        // }

        if (field === "gpm" && isManualEntry) {
            const gpmValue = parseFloat(value);
            const currentQuantity = parseInt(watch("quantity")) || 1;
            if (!isNaN(gpmValue) && gpmValue > 0) {
                const newFlowRate = String((gpmValue * currentQuantity).toFixed(2));
                setValue("flowRate", newFlowRate);
                onChangeEditForm({ gpm: value, flowRate: newFlowRate });
            } else {
                setValue("flowRate", "");
                onChangeEditForm({ gpm: value, flowRate: "" });
            }
        }

        // If DFU value changes and manual entry, recalculate GPM and flow rate
        if (field === "dfuValue" && isManualEntry) {
            const dfuVal = parseFloat(value);
            if (dfuVal && !isNaN(dfuVal)) {
                const newGpm = String(dfuVal / 2);
                const currentQuantity = watch("quantity") || 1;
                const newFlowRate = String(parseFloat((dfuVal / 2) * currentQuantity).toFixed(2));

                setValue("gpm", newGpm);
                setValue("flowRate", newFlowRate);
                onChangeEditForm({
                    gpm: newGpm,
                    flowRate: newFlowRate
                });
            } else {
                // Clear GPM and flow rate if DFU is invalid
                setValue("gpm", "");
                setValue("flowRate", "");
                onChangeEditForm({
                    gpm: "",
                    flowRate: ""
                });
            }
        }
    };

    const handleCustomSinkChange = (field, value) => {
        setValue(field, value);

        // Calculate new GPM if all values are present
        const formValues = { ...watch(), [field]: value };
        const l = parseFloat(formValues.length);
        const w = parseFloat(formValues.width);
        const d = parseFloat(formValues.depth);
        const n = parseFloat(formValues.compartments);

        let newGpm = "";
        let newFlowRate = "";

        if (!isNaN(l) && !isNaN(w) && !isNaN(d) && !isNaN(n) && l > 0 && w > 0 && d > 0 && n > 0) {
            // Formula: GPM = {[(L x W x D x N) / 231] x 0.75} / 2
            const calculated = (((l * w * d * n) / 231) * 0.75) / 2;
            newGpm = String(calculated.toFixed(2));

            // For custom sink, flow rate is equal to GPM (no quantity multiplier)
            newFlowRate = newGpm;

            setValue("gpm", newGpm);
            setValue("flowRate", newFlowRate);
            setValue("quantity", 1); // Ensure quantity is 1 internally
        } else {
            setValue("gpm", "");
            setValue("flowRate", "");
        }

        onChangeEditForm({
            [field]: value,
            ...(newGpm && { gpm: newGpm }),
            ...(newFlowRate && { flowRate: newFlowRate })
        });
    };

    const handleInputChange = (field, value) => {
        setValue(field, value);
        onChangeEditForm({ [field]: value });
    };

    return (
        <form
            onSubmit={handleSubmit(handleFormSubmit)}
            style={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
            }}
        >
            <div className="addFixtureForm" style={{
                display: "flex",
                gap: 12,
                padding: 15,
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: 8,
                margin: "15px 0",
                flexWrap: "wrap",
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                WebkitOverflowScrolling: "touch"
            }}>

                {/* Include Checkbox - ALWAYS VISIBLE */}
                <div className="" style={{ marginBottom: 0, minWidth: "auto" }}>
                    <label style={{
                        display: "flex",
                        flexDirection: 'column',
                        alignItems: "start",
                        cursor: "pointer",
                        userSelect: "none",
                        fontSize: 15,
                        marginBottom: '8px',
                        color: '#676666',
                    }}>
                        Include
                    </label>
                    <input
                        type="checkbox"
                        checked={!!editForm.included}
                        onChange={(e) => {
                            handleInputChange("included", e.target.checked);
                        }}
                        style={{
                            width: 20,
                            height: 20,
                            cursor: "pointer",
                        }}
                    />
                </div>

                {/* Fixture Type - ALWAYS VISIBLE */}
                <div style={{ flex: "0 0 250px" }}>
                    <SelectBox
                        control={control}
                        name="fixtureType"
                        label="Fixture Type *"
                        selectList={fixtureTypesList}
                        onChange={handleFixtureTypeChange}
                    />
                </div>

                {/* Show all other fields ONLY when fixture type is selected */}
                {showAllFields && (
                    <>
                        {!isCustomSink && (
                            <>
                                {/* ✅ UPDATED: Fixture Name - EDITABLE IF "OTHER" */}
                                <div style={{ minWidth: 160, flex: "0 0 160px" }}>
                                    <InputBox
                                        label="Fixture Name *"
                                        name="fixtureName"
                                        placeholder={isManualEntry ? "Enter fixture name" : "Fixture Name"}
                                        readOnly={!isManualEntry}
                                        control={control}
                                        onChange={(e) => handleManualInputChange("fixtureName", e.target.value)}
                                    />
                                </div>

                                {/* ✅ UPDATED: Trap Size - EDITABLE IF "OTHER" */}
                                <div style={{ minWidth: 120, flex: "0 0 120px" }}>
                                    <InputBox
                                        label="Trap Size (inch) *"
                                        name="trapSize"
                                        placeholder={isManualEntry ? "e.g. 1½" : "Trap Size"}
                                        readOnly={!isManualEntry}
                                        control={control}
                                        onChange={(e) => handleManualInputChange("trapSize", e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        {isCustomSink && (
                            <>
                                <div style={{ minWidth: 100, flex: "0 0 100px" }}>
                                    <InputBox
                                        label="Length (L) *"
                                        name="length"
                                        placeholder="Inches"
                                        control={control}
                                        type="number"
                                        onChange={(e) => handleCustomSinkChange("length", e.target.value)}
                                    />
                                </div>
                                <div style={{ minWidth: 100, flex: "0 0 100px" }}>
                                    <InputBox
                                        label="Width (W) *"
                                        name="width"
                                        placeholder="Inches"
                                        control={control}
                                        type="number"
                                        onChange={(e) => handleCustomSinkChange("width", e.target.value)}
                                    />
                                </div>
                                <div style={{ minWidth: 100, flex: "0 0 100px" }}>
                                    <InputBox
                                        label="Depth (D) *"
                                        name="depth"
                                        placeholder="Inches"
                                        control={control}
                                        type="number"
                                        onChange={(e) => handleCustomSinkChange("depth", e.target.value)}
                                    />
                                </div>
                                <div style={{ minWidth: 160, flex: "0 0 160px" }}>
                                    <InputBox
                                        label="Compartments (N) *"
                                        name="compartments"
                                        placeholder="Count"
                                        control={control}
                                        type="number"
                                        onChange={(e) => handleCustomSinkChange("compartments", e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        {/* ✅ UPDATED: DFU Type - HIDE IF CUSTOM SINK */}
                        {!isCustomSink && (
                            <>
                                <div style={{ minWidth: 150, flex: "0 0 150px" }}>
                                    {isManualEntry ? (
                                        <InputBox
                                            label="DFU Type *"
                                            name="dfuType"
                                            placeholder="Private/Public"
                                            control={control}
                                            onChange={(e) => handleManualInputChange("dfuType", e.target.value)}
                                        />
                                    ) : (
                                        <SelectBox
                                            control={control}
                                            name="dfuType"
                                            label="DFU Type *"
                                            selectList={dfuOptions}
                                            onChange={handleDfuTypeChange}
                                        />
                                    )}
                                </div>

                                {/* ✅ UPDATED: DFU Value - EDITABLE IF "OTHER" */}
                                <div style={{ minWidth: 100, flex: "0 0 100px" }}>
                                    <InputBox
                                        label="DFU Value *"
                                        name="dfuValue"
                                        placeholder="Enter DFU"
                                        control={control}
                                        readOnly={!isManualEntry}
                                        value={watch("dfuValue")}
                                        onChange={(e) => handleManualInputChange("dfuValue", e.target.value)}
                                        style={!isManualEntry ? {
                                            backgroundColor: "#f8f9fa",
                                            cursor: "not-allowed"
                                        } : {}}
                                    />
                                </div>
                            </>
                        )}

                        {/* ✅ UPDATED: GPM - HIDE FOR CUSTOM SINK */}
                        {!isCustomSink && (
                            <div style={{ minWidth: 100, flex: "0 0 100px" }}>
                                <InputBox
                                    label="GPM *"
                                    name="gpm"
                                    placeholder="Enter GPM"
                                    control={control}
                                    readOnly={!isManualEntry}
                                    value={watch("gpm")}
                                    // onChange={(e) => handleManualInputChange("gpm", e.target.value)}
                                    onChange={(e) => {
                                        const val = e?.target?.value ?? e;
                                        handleManualInputChange("gpm", val);
                                    }}
                                    style={!isManualEntry ? {
                                        backgroundColor: "#f8f9fa",
                                        cursor: "not-allowed"
                                    } : {}}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Quantity - HIDE FOR CUSTOM SINK */}
                {!isCustomSink && (
                    <div className="form-group" style={{
                        marginBottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 90,
                        flex: "0 0 90px"
                    }}>
                        <label className="form-label" style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#495057",
                            marginBottom: 8,
                            display: "block"
                        }}>
                            Quantity *
                        </label>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            height: 38,
                            border: "1px solid #ced4da",
                            borderRadius: 4,
                            background: "#fff",
                            padding: "0 4px",
                            width: 90
                        }}>
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(-1)}
                                style={{
                                    width: 20,
                                    height: 20,
                                    border: "none",
                                    borderRadius: 3,
                                    background: "#f8f9fa",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    flexShrink: 0
                                }}
                            >
                                −
                            </button>
                            <input
                                type="number"
                                value={watch("quantity") !== undefined && watch("quantity") !== null ? watch("quantity") : 1}
                                onChange={(e) => handleQuantityInputChange(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                min="1"
                                style={{
                                    flex: 1,
                                    textAlign: "center",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#495057",
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    width: "30px",
                                    padding: "0 2px",
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'textfield'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(1)}
                                style={{
                                    width: 20,
                                    height: 20,
                                    border: "none",
                                    borderRadius: 3,
                                    background: "#f8f9fa",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    flexShrink: 0
                                }}
                            >
                                +
                            </button>
                        </div>
                        {errors.quantity && (
                            <span className="error" style={{ fontSize: 12, color: "#dc3545", marginTop: 4 }}>
                                {errors.quantity.message}
                            </span>
                        )}
                    </div>
                )}

                {/* Show remaining fields ONLY when fixture type is selected */}
                {showAllFields && (
                    <>
                        {/* Flow Rate - ALWAYS READ-ONLY (AUTO-CALCULATED) */}
                        <div style={{ minWidth: 140, flex: "0 0 140px" }}>
                            <InputBox
                                label="Flow Rate (GPM) *"
                                name="flowRate"
                                placeholder="Total GPM"
                                control={control}
                                readOnly
                                value={watch("flowRate")}
                                style={{
                                    backgroundColor: "#f8f9fa",
                                    cursor: "not-allowed"
                                }}
                            />
                        </div>
                    </>
                )}

                {/* Actions - ALWAYS VISIBLE */}
                <div className="form-group" style={{
                    marginBottom: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flex: "0 0 300px"
                }}>
                    <label className="form-label" style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#495057",
                        marginBottom: 8,
                        display: "block"
                    }}>
                        Actions
                    </label>
                    <div style={{ display: "flex", flexDirection: "row", gap: 8, width: "100%" }}>
                        <button
                            type="submit"
                            style={{
                                width: "100%",
                                border: "none",
                                borderRadius: "40px",
                                background: "#4a76a8",
                                color: "#fff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "8px 20px",
                                fontSize: 14,
                                fontWeight: 500,
                                transition: "background-color 0.15s ease"
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = "#3d6290"}
                            onMouseOut={(e) => e.currentTarget.style.background = "#4a76a8"}
                        >
                            Add Fixture
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            style={{
                                width: "100%",
                                border: "none",
                                borderRadius: "40px",
                                background: "#4a76a8",
                                color: "#fff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "8px 20px",
                                fontSize: 14,
                                fontWeight: 500,
                                transition: "background-color 0.15s ease"
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = "#3d6290"}
                            onMouseOut={(e) => e.currentTarget.style.background = "#4a76a8"}
                        >
                            Remove Fixture
                        </button>
                    </div>
                </div>

            </div>
            {isCustomSink && (
                <div style={{ marginTop: 20, padding: 15, backgroundColor: "#e9ecef", borderRadius: 8, fontSize: 13, color: "#495057", lineHeight: "1.5" }}>
                    <div
                        style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                        onClick={() => setShowCalculationInfo(!showCalculationInfo)}
                    >
                        <strong style={{ fontSize: 14 }}>How Custom Sink Sizing Works</strong>
                        <span style={{ marginLeft: 8, fontSize: 12, transition: "transform 0.2s", transform: showCalculationInfo ? "rotate(180deg)" : "rotate(0deg)" }}>
                            ▼
                        </span>
                    </div>

                    {showCalculationInfo && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #ced4da" }}>
                            <p style={{ margin: "0 0 8px 0" }}>
                                The calculations are based on PDI guidelines:
                            </p>
                            <ul style={{ margin: "0 0 10px 20px", padding: 0 }}>
                                <li>Fixture Volume in inches (L x W x D)</li>
                                <li>Account for 75% volume capacity</li>
                                <li>Account for 2 minute drainage time</li>
                            </ul>
                            <div style={{ margin: "10px 0", fontFamily: "monospace", backgroundColor: "#fff", padding: "8px 12px", borderRadius: 4, display: "inline-block", border: "1px solid #ced4da" }}>
                                {`{[(L x W x D x N) / 231] x 0.75} / 2 minutes = fixture flow GPM`}
                            </div>
                            <ul style={{ margin: "10px 0 0 20px", padding: 0, listStyle: "none" }}>
                                <li style={{ marginBottom: 4 }}><strong>L</strong> = Length</li>
                                <li style={{ marginBottom: 4 }}><strong>W</strong> = Width</li>
                                <li style={{ marginBottom: 4 }}><strong>D</strong> = Depth</li>
                                <li style={{ marginBottom: 4 }}><strong>N</strong> = Number of equally sized compartments</li>
                                <li style={{ marginBottom: 4 }}><strong>231</strong> = converting factor from cubic inches to gallons</li>
                                <li style={{ marginBottom: 4 }}><strong>0.75</strong> = 75% fixture adjusted capacity</li>
                                <li style={{ marginBottom: 0 }}><strong>2 minutes</strong> = drainage time</li>
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </form>
    );
});




